import {
  faGrid2,
  faList,
  type IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { Button, Icon, Section } from "@intility/bifrost-react";
import type { ReactNode } from "react";
import s from "./MyPlatforms.module.css";

export type HubView = "list" | "grid";

// A section of My Hub (Favorites, All platforms, All clients): a header with a
// list/grid switcher, then either the table or the cards. With an `empty`
// message and nothing in the section, the message is shown in the card area.
export function MyHubSection({
  title,
  icon,
  view,
  onViewChange,
  empty,
  isEmpty,
  list,
  cards,
}: {
  title: string;
  icon: IconDefinition;
  view: HubView;
  onViewChange: (view: HubView) => void;
  /** Shown instead of the table or the cards when there is nothing to list. */
  empty?: ReactNode;
  isEmpty: boolean;
  /** The table. */
  list: ReactNode;
  /** The cards. */
  cards: ReactNode;
}) {
  const header = (
    <Section.Header style={{ backgroundColor: "var(--bfc-base-3)" }}>
      <div
        style={{
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>
          <Icon icon={icon} style={{ marginRight: 8 }} />
          {title}
        </span>
        <Button.Group>
          <Button
            active={view === "list"}
            aria-label={`List view of ${title}`}
            aria-pressed={view === "list"}
            noPadding
            onClick={() => onViewChange("list")}
            small
            variant={view === "list" ? "filled" : "flat"}
          >
            <Icon icon={faList} />
          </Button>
          <Button
            active={view === "grid"}
            aria-label={`Grid view of ${title}`}
            aria-pressed={view === "grid"}
            noPadding
            onClick={() => onViewChange("grid")}
            small
            variant={view === "grid" ? "filled" : "flat"}
          >
            <Icon icon={faGrid2} />
          </Button>
        </Button.Group>
      </div>
    </Section.Header>
  );

  if (view === "list" && !(empty && isEmpty)) {
    return (
      <div className={s.table} style={{ marginTop: 24 }}>
        {header}
        {list}
      </div>
    );
  }

  return (
    <Section shadow={false} style={{ marginTop: 24 }}>
      {header}
      <Section.Content style={{ backgroundColor: "var(--bfc-base-2)" }}>
        {empty && isEmpty ? empty : cards}
      </Section.Content>
    </Section>
  );
}
